import { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useIntl } from 'react-intl';
import { CustomThemeProvider, ModalUI, Switch, usePortal } from 'sonata-react-components';

import { CsvDropzone } from '@/components/common/CsvDropzone/CsvDropzone';
import { useMultiFlightMap } from '../../MultiFlightMap.context';
import { CountDown } from '../AttributionPOI/CountDown/CountDown';
import { useLocationCSVUploadModalContext } from './context/LocationCSVUploadModalContext';
import { LocationCSVUploadModalStyled } from './LocationCSVUploadModal.styles';
import { LocationImporter } from './LocationImporter';

type Props = {
  id: string;
  isModalOpened: boolean;
  onClose: () => void;
  theme?: 'dark' | 'light';
};

type State = {
  isApplying: boolean;
  countdownShown: boolean;
};

export const LocationCSVUploadModal = function ({ id, isModalOpened, onClose, theme = 'dark' }: Props) {
  const { formatMessage } = useIntl();
  const modalRef = useRef<React.ElementRef<typeof ModalUI>>(null);
  const targetBody = usePortal('locationCSVUploadModal');
  const { file, setFile } = useLocationCSVUploadModalContext();
  const { cancelBatchUploadFile, processBatchUploadFile, processBatchUploadFileStatus } = useMultiFlightMap();
  const wasProcessingRef = useRef(false);
  const [{ isApplying, countdownShown }, setState] = useState<State>({
    countdownShown: false,
    isApplying: false
  });

  const templateCsv = `${taptap.config.sonataStaticsUrlBase}/samples/locationImporter/exampleZones.csv`;
  const isProcessing = processBatchUploadFileStatus === 'processing';
  const isProcessDone =
    processBatchUploadFileStatus === 'success' || processBatchUploadFileStatus === 'partial-success';

  useEffect(() => {
    if (isModalOpened) {
      modalRef?.current?.openModal?.();
    } else {
      modalRef?.current?.closeModal?.();
    }
  }, [isModalOpened]);

  useEffect(() => {
    if (isProcessing) {
      wasProcessingRef.current = true;
      return;
    }
    if (isProcessDone && wasProcessingRef.current) {
      wasProcessingRef.current = false;
      setState(prev => ({ ...prev, countdownShown: false }));
      onClose();
    }
  }, [isProcessing, isProcessDone]);

  const handleApply = async (payload: Parameters<typeof processBatchUploadFile>[1]) => {
    setState(prev => ({ ...prev, countdownShown: true, isApplying: true }));
    await processBatchUploadFile('zone', payload);
    setState(prev => ({ ...prev, isApplying: false }));
  };

  const handleCancelProcess = async () => {
    await cancelBatchUploadFile();
    onClose();
  };

  return ReactDOM.createPortal(
    <CustomThemeProvider themeContrast={theme}>
      <LocationCSVUploadModalStyled data-testid="location-csv-upload-modal-container">
        <ModalUI
          closeModalCallback={onClose}
          description={formatMessage({
            defaultMessage: 'Upload your CSV file with the desired areas address',
            id: 'entityFileUpload.locationsDescription'
          })}
          id={id}
          ref={modalRef}
          title={formatMessage({ defaultMessage: 'Address & radius file', id: 'entityFileUpload.locationsTitle' })}
          width="1152px"
        >
          <Switch>
            <Switch.Case condition={isProcessing}>
              <CountDown
                countdownShown={countdownShown}
                onCancel={handleCancelProcess}
                onFinish={onClose}
                onMinimize={onClose}
              />
            </Switch.Case>
            <Switch.Case condition={Boolean(file)}>
              <LocationImporter isApplying={isApplying} onApply={handleApply} onGoBack={() => setFile(undefined)} />
            </Switch.Case>
            <Switch.Default>
              <CsvDropzone
                inputId="location-csv"
                onFile={setFile}
                templateUrl={templateCsv}
                uploadLabel={formatMessage({
                  defaultMessage: 'Upload data file',
                  id: 'attributionPOI.modal.dropzone.uploadButton'
                })}
              />
            </Switch.Default>
          </Switch>
        </ModalUI>
      </LocationCSVUploadModalStyled>
    </CustomThemeProvider>,
    targetBody
  );
};
